import React, { useMemo } from "react";
import styles from "./style.module.scss";
import { useSelector } from "react-redux";
import { RootState } from "../../../state";
import { Line } from "react-chartjs-2";

import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  LineElement,
  PointElement,
  ChartDataset,
} from "chart.js";
import annotationPlugin from "chartjs-plugin-annotation";
import { isEqual } from "lodash";
import { linearInterpolate } from "../../../utils/maths";
import { getPreferredFormattedTimeSeconds } from "../../../utils/preferences";

ChartJS.register(
  CategoryScale,
  LinearScale,
  LineElement,
  PointElement,
  annotationPlugin,
);

const TRGT_RANGE = 0.3;
const TRGT_STEP = 0.05;

const RACE_COLOR = "#00ff59";
const ATTACK_COLOR = "#ff00f2";

function TRGTFrontiersGraph({ carColour }: { carColour: string }) {
  const emImport = useSelector(
    (state: RootState) => state.imports?.imports?.em?.data,
    isEqual,
  );

  const raceConfig = useSelector(
    (state: RootState) => state.raceData[carColour]?.raceConfig,
    isEqual,
  );

  const targetRace = raceConfig?.targetRace ?? null;
  const targetAttack = raceConfig?.targetAttack ?? null;

  const frontierRaceMap3 = emImport?.FrontierRaceMap3;
  const frontierAttackMap2 = emImport?.FrontierAttackMap2;

  // Widest relative offset (from each curve's own target) needed to show both curves in full
  const xAxisRange = useMemo(() => {
    const raceTargets = frontierRaceMap3?.[1];
    const attackTargets = frontierAttackMap2?.[1];
    if (!raceTargets?.length || !attackTargets?.length) {
      return { min: -TRGT_RANGE, max: TRGT_RANGE };
    }

    const raceTarget = targetRace ?? 0;
    const attackTarget = targetAttack ?? 0;

    return {
      min: Math.min(
        Math.min(...raceTargets) - raceTarget,
        Math.min(...attackTargets) - attackTarget,
      ),
      max: Math.max(
        Math.max(...raceTargets) - raceTarget,
        Math.max(...attackTargets) - attackTarget,
      ),
    };
  }, [frontierRaceMap3, frontierAttackMap2, targetRace, targetAttack]);

  const chartData = useMemo(() => {
    if (!frontierRaceMap3 || !frontierAttackMap2) {
      return {
        labels: [],
        datasets: [],
      };
    }

    const datasets: ChartDataset<"line">[] = [];

    const negSteps = Math.ceil(Math.max(0, -xAxisRange.min) / TRGT_STEP);
    const posSteps = Math.ceil(Math.max(0, xAxisRange.max) / TRGT_STEP);
    const relativeXs: number[] = [];
    for (let i = -negSteps; i <= posSteps; i++) {
      relativeXs.push(i * TRGT_STEP);
    }

    // Resample a frontier curve at fixed ENO increments, relative to its current target
    const buildFrontierCurve = (
      xp: number[],
      fp: number[],
      xOffset: number,
    ): { x: number; y: number }[] => {
      const curveMin = Math.min(...xp);
      const curveMax = Math.max(...xp);
      return relativeXs.map((relativeX) => {
        const absoluteX = relativeX + xOffset;
        return absoluteX < curveMin || absoluteX > curveMax
          ? { x: relativeX, y: NaN }
          : { x: relativeX, y: linearInterpolate(absoluteX, xp, fp) };
      });
    };

    const raceMap3Data = buildFrontierCurve(
      frontierRaceMap3[1],
      frontierRaceMap3[0],
      targetRace ?? 0,
    );

    const attackMap2Data = buildFrontierCurve(
      frontierAttackMap2[1],
      frontierAttackMap2[0],
      targetAttack ?? 0,
    );

    datasets.push({
      label: "Race",
      data: raceMap3Data,
      pointRadius: 2,
      pointHoverRadius: 5,
      backgroundColor: RACE_COLOR,
      borderColor: RACE_COLOR,
      borderWidth: 2,
      showLine: true,
      spanGaps: false,
    });

    datasets.push({
      label: "Attack",
      data: attackMap2Data,
      pointRadius: 2,
      pointHoverRadius: 5,
      backgroundColor: ATTACK_COLOR,
      borderColor: ATTACK_COLOR,
      borderWidth: 2,
      showLine: true,
      spanGaps: false,
    });

    return {
      labels: [],
      datasets,
    };
  }, [
    frontierRaceMap3,
    frontierAttackMap2,
    targetRace,
    targetAttack,
    xAxisRange,
  ]);

  // Lap time at the current target ENO, per power mode's own frontier curve
  const targetRaceLapTime = useMemo(() => {
    if (targetRace === null || !frontierRaceMap3) return null;
    return linearInterpolate(
      targetRace,
      frontierRaceMap3[1],
      frontierRaceMap3[0],
    );
  }, [targetRace, frontierRaceMap3]);

  const targetAttackLapTime = useMemo(() => {
    if (targetAttack === null || !frontierAttackMap2) return null;
    return linearInterpolate(
      targetAttack,
      frontierAttackMap2[1],
      frontierAttackMap2[0],
    );
  }, [targetAttack, frontierAttackMap2]);

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    animation: false as const,
    clip: false as const,
    plugins: {
      datalabels: {
        display: false,
      },
      legend: {
        display: true,
        position: "top" as const,
        labels: {
          color: "#ffffff",
          usePointStyle: true,
          pointStyle: "line" as const,
        },
      },
      tooltip: {
        callbacks: {
          label: (context: {
            dataset: { label?: string };
            parsed: { x: number; y: number };
          }) => {
            const isRace = context.dataset.label === "Race";
            const targetLapTime = isRace
              ? targetRaceLapTime
              : targetAttackLapTime;
            const target = (isRace ? targetRace : targetAttack) ?? 0;
            const targetWithENO = context.parsed.x + target;
            const delta = context.parsed.y - (targetLapTime ?? 0);
            return [
              `Target: ${targetWithENO.toFixed(2)}`,
              `Δ LT: ${delta >= 0 ? "+" : ""}${delta.toFixed(3)}`,
            ];
          },
        },
      },
      annotation: {
        annotations: {
          currentTargetLine: {
            type: "line" as const,
            xMin: 0,
            xMax: 0,
            borderColor: "#ffffff",
            borderWidth: 2,
          },
          ...(targetRace !== null && targetRaceLapTime !== null
            ? {
                targetRaceMarker: {
                  type: "point" as const,
                  xValue: 0,
                  yValue: targetRaceLapTime,
                  backgroundColor: RACE_COLOR,
                  borderWidth: 0,
                  radius: 5,
                },
              }
            : {}),
          ...(targetAttack !== null && targetAttackLapTime !== null
            ? {
                targetAttackMarker: {
                  type: "point" as const,
                  xValue: 0,
                  yValue: targetAttackLapTime,
                  backgroundColor: ATTACK_COLOR,
                  borderWidth: 0,
                  radius: 5,
                },
              }
            : {}),
        },
      },
      title: {
        display: true,
        text: "TRGT Frontiers",
        color: "#ffffff",
        font: {
          size: 16,
          weight: "bold" as const,
        },
      },
    },
    scales: {
      x: {
        type: "linear" as const,
        position: "bottom" as const,
        border: {
          color: "#ffffff",
        },
        grid: {
          color: "rgba(255, 255, 255, 0.1)",
        },
        ticks: {
          color: "#ffffff",
          stepSize: TRGT_STEP,
        },
        title: {
          display: true,
          text: "ENO",
          color: "#ffffff",
        },
        min: xAxisRange.min,
        max: xAxisRange.max,
      },
      y: {
        grid: {
          color: "rgba(255, 255, 255, 0.1)",
        },
        ticks: {
          color: "#ffffff",
          callback: (value: string | number) =>
            getPreferredFormattedTimeSeconds(Number(value), false),
        },
        border: {
          color: "#ffffff",
        },
        title: {
          display: true,
          text: "Lap Time (s)",
          color: "#ffffff",
        },
      },
    },
  };

  return (
    <div className={styles.container}>
      <Line data={chartData} options={chartOptions} />
    </div>
  );
}

export default TRGTFrontiersGraph;
